import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, formatDate } from '@/lib/api';
import { sanitizeHtml, safeHtmlProps } from '@/lib/sanitize';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileText, Envelope, WhatsappLogo, Printer, DownloadSimple, Trash, PencilSimple, Eye, TextB, TextItalic, TextUnderline, TextAlignRight, TextAlignCenter, TextAlignLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const VARIABLES = [
  { key: 'tenant_name', label: 'اسم المستأجر' },
  { key: 'payment_no', label: 'رقم الدفعة' },
  { key: 'invoice_no', label: 'رقم الفاتورة' },
  { key: 'property_name', label: 'اسم العقار' },
  { key: 'branch', label: 'الفرع' },
  { key: 'contract_no', label: 'رقم العقد' },
  { key: 'invoice_amount', label: 'مبلغ الفاتورة' },
  { key: 'remaining_amount', label: 'المبلغ المتبقي' },
  { key: 'due_date', label: 'تاريخ الاستحقاق' },
  { key: 'today', label: 'تاريخ اليوم' },
  { key: 'phone', label: 'رقم الجوال' },
  { key: 'national_id', label: 'رقم الهوية' },
];

const RichEditor = ({ value, onChange, testId }) => {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ref.current && !ready) {
      ref.current.innerHTML = sanitizeHtml(value || '');
      setReady(true);
    }
  }, [value, ready]);

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
  };

  const insertText = (t) => {
    document.execCommand('insertText', false, t);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  return (
    <div className="border border-slate-300 rounded-md bg-white">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-slate-200 bg-slate-50">
        <button type="button" onClick={() => exec('bold')} className="p-1.5 rounded hover:bg-slate-200" title="عريض"><TextB size={16} /></button>
        <button type="button" onClick={() => exec('italic')} className="p-1.5 rounded hover:bg-slate-200" title="مائل"><TextItalic size={16} /></button>
        <button type="button" onClick={() => exec('underline')} className="p-1.5 rounded hover:bg-slate-200" title="تسطير"><TextUnderline size={16} /></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={() => exec('justifyRight')} className="p-1.5 rounded hover:bg-slate-200"><TextAlignRight size={16} /></button>
        <button type="button" onClick={() => exec('justifyCenter')} className="p-1.5 rounded hover:bg-slate-200"><TextAlignCenter size={16} /></button>
        <button type="button" onClick={() => exec('justifyLeft')} className="p-1.5 rounded hover:bg-slate-200"><TextAlignLeft size={16} /></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <select onChange={(e) => { exec('fontSize', e.target.value); e.target.value = ''; }} className="text-xs h-7 rounded border border-slate-300 px-1">
          <option value="">حجم الخط</option>
          <option value="2">صغير</option>
          <option value="3">عادي</option>
          <option value="4">متوسط</option>
          <option value="5">كبير</option>
          <option value="6">كبير جداً</option>
        </select>
        <select onChange={(e) => { exec('formatBlock', e.target.value); e.target.value = ''; }} className="text-xs h-7 rounded border border-slate-300 px-1">
          <option value="">نوع الفقرة</option>
          <option value="h1">عنوان 1</option>
          <option value="h2">عنوان 2</option>
          <option value="h3">عنوان 3</option>
          <option value="p">فقرة</option>
        </select>
        <input type="color" onChange={(e) => exec('foreColor', e.target.value)} className="w-7 h-7 rounded cursor-pointer" title="لون النص" />
        <button type="button" onClick={() => exec('insertUnorderedList')} className="p-1.5 rounded hover:bg-slate-200 text-xs">• قائمة</button>
        <button type="button" onClick={() => exec('insertHTML', '<hr/>')} className="p-1.5 rounded hover:bg-slate-200 text-xs">—</button>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="min-h-[300px] p-4 outline-none prose prose-sm max-w-none leading-[2]"
        dir="rtl"
        data-testid={testId}
        style={{ fontFamily: "'IBM Plex Sans Arabic', Tahoma, sans-serif", fontSize: 15 }}
      />
      <div className="border-t border-slate-200 p-2 bg-slate-50">
        <div className="text-xs text-slate-500 mb-1.5">إدراج حقل ديناميكي:</div>
        <div className="flex flex-wrap gap-1">
          {VARIABLES.map(v => (
            <button
              key={v.key}
              type="button"
              onClick={() => insertText(`{{${v.key}}}`)}
              className="text-xs px-2 py-0.5 rounded-full bg-white border border-slate-200 hover:bg-[#B4935A] hover:text-white hover:border-[#B4935A]"
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Letters() {
  const [params] = useSearchParams();
  const preselectedInvoice = params.get('invoice_id');

  const [letters, setLetters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('letters'); // letters | templates | settings
  const [openLetter, setOpenLetter] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(null); // letter obj
  const [form, setForm] = useState({ title: '', category: 'custom', body_html: '', invoice_id: preselectedInvoice || '', tenant_id: '' });
  const [openTemplate, setOpenTemplate] = useState(false);
  const [tplForm, setTplForm] = useState({ title: '', category: 'custom', body_html: '' });
  const [editingTpl, setEditingTpl] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoices, setInvoices] = useState([]);

  const loadAll = () => {
    api.get('/letters').then(({ data }) => setLetters(data));
    api.get('/letter-templates').then(({ data }) => setTemplates(data));
    api.get('/letter-categories').then(({ data }) => setCategories(data));
    api.get('/company-settings').then(({ data }) => setSettings(data));
    api.get('/invoices').then(({ data }) => setInvoices(data));
  };
  useEffect(() => { loadAll(); }, []);

  const catLabel = (code) => categories.find(c => c.code === code)?.label || code;

  const useTemplate = (tid) => {
    const t = templates.find(x => x.id === tid);
    if (!t) return;
    setForm(f => ({ ...f, title: t.title, category: t.category, body_html: t.body_html }));
  };

  const createLetter = async () => {
    if (!form.title || !form.body_html) { toast.error('العنوان والنص مطلوبان'); return; }
    try {
      const { data } = await api.post('/letters', form);
      toast.success(`تم إنشاء الخطاب ${data.letter_no}`);
      setOpenLetter(false);
      setForm({ title: '', category: 'custom', body_html: '', invoice_id: '', tenant_id: '' });
      loadAll();
      setPreviewOpen(data);
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
  };

  const openPrint = (letter) => {
    // Server-side PDF generation with logo, header, footer, page numbers
    const token = localStorage.getItem('token');
    const url = `${api.defaults.baseURL}/letters/${letter.id}/pdf?auth=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
    setTimeout(() => api.post(`/letters/${letter.id}/mark-sent`, { via: 'downloaded' }).then(loadAll), 500);
  };

  const shareWA = (letter) => {
    if (!letter.invoice_id) { toast.error('اربط الخطاب بفاتورة أولاً'); return; }
    const inv = invoices.find(i => i.id === letter.invoice_id);
    if (!inv?.phone) { toast.error('لا يوجد جوال في الفاتورة'); return; }
    const stripped = new DOMParser().parseFromString(letter.body_html, 'text/html').body.textContent || '';
    const msg = `${letter.title}\n\nرقم الخطاب: ${letter.letter_no}\n\n${stripped}`;
    window.open(`https://wa.me/${String(inv.phone).replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`, '_blank');
    api.post(`/letters/${letter.id}/mark-sent`, { via: 'whatsapp' }).then(loadAll);
  };

  const emailLetter = (letter) => {
    const inv = letter.invoice_id ? invoices.find(i => i.id === letter.invoice_id) : null;
    const to = inv?.email || '';
    const subject = encodeURIComponent(`${letter.title} — ${letter.letter_no}`);
    const stripped = new DOMParser().parseFromString(letter.body_html, 'text/html').body.textContent || '';
    const body = encodeURIComponent(stripped);
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_blank');
    api.post(`/letters/${letter.id}/mark-sent`, { via: 'email' }).then(loadAll);
  };

  const del = async (id) => {
    if (!window.confirm('حذف الخطاب؟')) return;
    await api.delete(`/letters/${id}`);
    loadAll();
  };

  const saveTemplate = async () => {
    if (!tplForm.title || !tplForm.body_html) { toast.error('العنوان والنص مطلوبان'); return; }
    try {
      if (editingTpl) await api.put(`/letter-templates/${editingTpl.id}`, tplForm);
      else await api.post('/letter-templates', tplForm);
      toast.success('تم الحفظ');
      setOpenTemplate(false); setEditingTpl(null);
      setTplForm({ title: '', category: 'custom', body_html: '' });
      loadAll();
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
  };

  const editTemplate = (t) => { setEditingTpl(t); setTplForm({ title: t.title, category: t.category, body_html: t.body_html }); setOpenTemplate(true); };
  const applyTemplateToNewLetter = (t) => { setForm({ title: t.title, category: t.category, body_html: t.body_html, invoice_id: '', tenant_id: '' }); setOpenLetter(true); };

  const saveSettings = async () => {
    await api.put('/company-settings', settings);
    toast.success('تم حفظ الإعدادات');
  };

  const filteredInvoices = invoiceSearch
    ? invoices.filter(i => Object.values(i).some(v => String(v || '').toLowerCase().includes(invoiceSearch.toLowerCase()))).slice(0, 30)
    : invoices.slice(0, 30);

  return (
    <div className="space-y-6" data-testid="letters-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Letters & Notices</div>
          <h1 className="text-3xl font-bold text-slate-900">الخطابات والإشعارات</h1>
          <p className="text-sm text-slate-500 mt-1">إنشاء وإدارة الخطابات الرسمية مع ترقيم تلقائي، محرر احترافي، ومشاركة عبر البريد/واتساب/طباعة</p>
        </div>
        <div className="flex gap-2">
          {tab === 'letters' && (
            <Button onClick={() => setOpenLetter(true)} className="bg-slate-900 text-white" data-testid="new-letter-button"><Plus className="ms-1" size={16} /> خطاب جديد</Button>
          )}
          {tab === 'templates' && (
            <Button onClick={() => { setEditingTpl(null); setTplForm({ title: '', category: 'custom', body_html: '' }); setOpenTemplate(true); }} className="bg-slate-900 text-white"><Plus className="ms-1" size={16} /> قالب جديد</Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[
          { id: 'letters', label: 'الخطابات' },
          { id: 'templates', label: 'القوالب' },
          { id: 'settings', label: 'إعدادات الخطاب' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-[#B4935A] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'letters' && (
        <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
          <table className="w-full text-sm data-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">رقم الخطاب</th>
                <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">العنوان</th>
                <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">النوع</th>
                <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">التاريخ</th>
                <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">أنشأه</th>
                <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">أُرسل عبر</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {letters.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-slate-500">لا توجد خطابات بعد. اضغط "خطاب جديد" لإنشاء أول خطاب.</td></tr>
              ) : letters.map(l => (
                <tr key={l.id} className="border-t border-slate-100" data-testid={`letter-${l.letter_no}`}>
                  <td className="p-3 mono font-medium">{l.letter_no}</td>
                  <td className="p-3">{l.title}</td>
                  <td className="p-3"><Badge variant="outline" className="border-slate-300 text-xs">{catLabel(l.category)}</Badge></td>
                  <td className="p-3 mono text-xs text-slate-600">{formatDate(l.created_at)}</td>
                  <td className="p-3 text-slate-600">{l.created_by_name}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {(l.sent_via || []).map(v => (
                        <Badge key={v} variant="outline" className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700">{v}</Badge>
                      ))}
                      {(!l.sent_via || l.sent_via.length === 0) && <span className="text-xs text-slate-400">مسودة</span>}
                    </div>
                  </td>
                  <td className="p-3 text-end">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setPreviewOpen(l)} title="عرض"><Eye size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openPrint(l)} title="طباعة"><Printer size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => shareWA(l)} title="واتساب"><WhatsappLogo size={16} className="text-emerald-600" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => emailLetter(l)} title="بريد"><Envelope size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(l.id)}><Trash size={16} className="text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'templates' && (
        <div className="grid md:grid-cols-2 gap-4">
          {templates.length === 0 ? (
            <Card className="p-10 text-center text-slate-500 col-span-2 border border-slate-200 rounded-md shadow-none">
              لا توجد قوالب. اضغط "قالب جديد" لإضافة قالب.
            </Card>
          ) : templates.map(t => (
            <Card key={t.id} className="p-5 border border-slate-200 rounded-md shadow-none">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-semibold text-slate-900">{t.title}</div>
                  <Badge variant="outline" className="text-xs border-slate-300 mt-1">{catLabel(t.category)}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => applyTemplateToNewLetter(t)} title="استخدام في خطاب"><FileText size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => editTemplate(t)}><PencilSimple size={16} /></Button>
                </div>
              </div>
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded p-3 max-h-32 overflow-hidden" {...safeHtmlProps(t.body_html)} />
            </Card>
          ))}
        </div>
      )}

      {tab === 'settings' && settings && (
        <Card className="p-6 border border-slate-200 rounded-md shadow-none">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label>اسم الشركة (عربي)</Label>
              <Input value={settings.company_name_ar || ''} onChange={(e) => setSettings({ ...settings, company_name_ar: e.target.value })} />
            </div>
            <div>
              <Label>اسم الشركة (English)</Label>
              <Input value={settings.company_name_en || ''} onChange={(e) => setSettings({ ...settings, company_name_en: e.target.value })} />
            </div>
            <div>
              <Label>رابط الشعار (URL)</Label>
              <Input value={settings.logo_url || ''} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label>بادئة رقم الخطاب</Label>
              <Input value={settings.letter_no_prefix || ''} onChange={(e) => setSettings({ ...settings, letter_no_prefix: e.target.value })} />
            </div>
            <div>
              <Label>اسم المُوقّع</Label>
              <Input value={settings.signature_name || ''} onChange={(e) => setSettings({ ...settings, signature_name: e.target.value })} />
            </div>
            <div>
              <Label>مسمى المُوقّع</Label>
              <Input value={settings.signature_title || ''} onChange={(e) => setSettings({ ...settings, signature_title: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>رأس الصفحة (HTML)</Label>
              <Textarea rows={3} value={settings.letter_header_html || ''} onChange={(e) => setSettings({ ...settings, letter_header_html: e.target.value })} className="mono text-xs" />
            </div>
            <div className="md:col-span-2">
              <Label>تذييل الصفحة (HTML)</Label>
              <Textarea rows={3} value={settings.letter_footer_html || ''} onChange={(e) => setSettings({ ...settings, letter_footer_html: e.target.value })} className="mono text-xs" />
            </div>
          </div>
          <Button onClick={saveSettings} className="mt-6 bg-slate-900 text-white">حفظ الإعدادات</Button>
        </Card>
      )}

      {/* New Letter Dialog */}
      <Dialog open={openLetter} onOpenChange={setOpenLetter}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>خطاب جديد</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label>عنوان الخطاب</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="مثال: إنذار أول — الفاتورة PAY-001" data-testid="letter-title" />
              </div>
              <div>
                <Label>النوع</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label>ابدأ من قالب (اختياري)</Label>
                <Select onValueChange={useTemplate}>
                  <SelectTrigger><SelectValue placeholder="اختر قالب..." /></SelectTrigger>
                  <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label>ربط بفاتورة (اختياري) — يُملأ المحتوى تلقائياً من بيانات الفاتورة</Label>
                <Input value={invoiceSearch} onChange={(e) => setInvoiceSearch(e.target.value)} placeholder="ابحث برقم الدفعة أو اسم المستأجر..." />
                {invoiceSearch && (
                  <div className="border border-slate-200 rounded max-h-40 overflow-y-auto mt-1">
                    {filteredInvoices.map(i => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => { setForm({ ...form, invoice_id: i.id }); setInvoiceSearch(`${i.payment_no} — ${i.tenant_name}`); }}
                        className="w-full text-start p-2 hover:bg-slate-50 border-b border-slate-100 text-sm"
                      >
                        <span className="mono">{i.payment_no}</span> — {i.tenant_name} — <span className="text-slate-500">{i.property_name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {form.invoice_id && <div className="text-xs text-emerald-600 mt-1">✓ مربوط بفاتورة</div>}
              </div>
            </div>
            <div>
              <Label>نص الخطاب</Label>
              <RichEditor value={form.body_html} onChange={(v) => setForm(f => ({ ...f, body_html: v }))} testId="letter-editor" />
            </div>
            <Button onClick={createLetter} className="w-full bg-slate-900 text-white h-11" data-testid="save-letter-button">
              حفظ وإنشاء رقم الخطاب
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={openTemplate} onOpenChange={setOpenTemplate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingTpl ? 'تعديل قالب' : 'قالب جديد'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>العنوان</Label>
                <Input value={tplForm.title} onChange={(e) => setTplForm({ ...tplForm, title: e.target.value })} />
              </div>
              <div>
                <Label>النوع</Label>
                <Select value={tplForm.category} onValueChange={(v) => setTplForm({ ...tplForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>نص القالب</Label>
              <RichEditor value={tplForm.body_html} onChange={(v) => setTplForm(f => ({ ...f, body_html: v }))} />
            </div>
            <Button onClick={saveTemplate} className="w-full bg-slate-900 text-white">حفظ القالب</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={!!previewOpen} onOpenChange={(v) => !v && setPreviewOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewOpen?.title}</DialogTitle>
            <DialogDescription>رقم الخطاب: <span className="mono">{previewOpen?.letter_no}</span></DialogDescription>
          </DialogHeader>
          {previewOpen && (
            <div className="border border-slate-200 rounded-md">
              <div className="border-b border-slate-200 p-4" {...safeHtmlProps(settings?.letter_header_html)} />
              <div className="p-6 leading-[2]" dir="rtl" {...safeHtmlProps(previewOpen.body_html)} />
              <div className="p-4 mt-4 border-t border-slate-200 text-sm">
                <div>{settings?.signature_name}</div>
                <div className="text-xs text-slate-500">{settings?.signature_title || 'إدارة التحصيل'}</div>
              </div>
              <div className="border-t border-slate-200 p-3 text-xs text-slate-500" {...safeHtmlProps(settings?.letter_footer_html)} />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => openPrint(previewOpen)} className="bg-slate-900 text-white"><Printer className="ms-1" size={16} /> طباعة / حفظ PDF</Button>
            <Button onClick={() => shareWA(previewOpen)} className="bg-emerald-600 text-white"><WhatsappLogo className="ms-1" size={16} /> واتساب</Button>
            <Button onClick={() => emailLetter(previewOpen)} variant="outline"><Envelope className="ms-1" size={16} /> بريد</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
